import {gql} from '@apollo/client';
import {useMutation} from '@apollo/client/react';
import {mapObjectToArray} from '@coolcolduk/util';
import {MutationCreateStripeCheckoutSessionArgs, PaymentMethodCollection} from '../__generated__/resolvers-types';

export const CREATE_CHECKOUT_SESSION_MUTATION = gql`
  mutation CreateStripeCheckoutSession(
    $priceId: String!
    $successUrl: String!
    $cancelUrl: String!
    $metadata: [String!]
    $allowPromotionCodes: Boolean
    $paymentMethodCollection: PaymentMethodCollection
  ) {
    createStripeCheckoutSession(
      priceId: $priceId
      successUrl: $successUrl
      cancelUrl: $cancelUrl
      metadata: $metadata
      allowPromotionCodes: $allowPromotionCodes
      paymentMethodCollection: $paymentMethodCollection
    ) {
      id
      price
      success_url
      cancel_url
      mode
      error
      url
      allow_promotion_codes
      payment_method_collection
    }
  }
`;

export default function useCreateCheckoutSession() {
  const [createStripeCheckoutSession, {data, loading, error}] = useMutation<
    {createStripeCheckoutSession: {url: string}},
    MutationCreateStripeCheckoutSessionArgs
  >(CREATE_CHECKOUT_SESSION_MUTATION);

  return {
    /**
     * Create a Stripe checkout session
     * @param priceId - The Stripe price ID
     * @param successUrl - URL to redirect to on successful payment
     * @param cancelUrl - URL to redirect to on cancelled payment
     * @param metadata - Optional metadata to attach to the session
     * @param allowPromotionCodes - Whether to allow promotion codes during checkout (default: false)
     * @param paymentMethodCollection - Payment method collection strategy: 'always' or 'if_required' (default: undefined)
     */
    createStripeCheckoutSession: async (
      priceId: string,
      successUrl: string,
      cancelUrl: string,
      metadata?: Record<string, string>,
      allowPromotionCodes?: boolean,
      paymentMethodCollection?: PaymentMethodCollection,
    ) =>
      createStripeCheckoutSession({
        variables: {
          priceId,
          successUrl,
          cancelUrl,
          metadata: metadata ? mapObjectToArray(metadata) : null,
          allowPromotionCodes: allowPromotionCodes || null,
          paymentMethodCollection: paymentMethodCollection || null,
        },
      }),
    data,
    loading,
    error,
  };
}
